import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { authSchemas, type SignInSchema } from '@/api/auth/auth.schema';
import { Button } from '@/components/ui/button';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { ThemeToggle } from '@/components/ui/toggle-theme';
import { CONFIG } from '@/config-globals';
import { animations } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useSignIn } from '@/services/auth';

export const Route = createFileRoute('/_auth/sign-in')({
  beforeLoad: ({ context: { auth } }) => {
    if (auth.isAuthenticated) {
      return redirect({ to: '/' });
    }
  },
  component: RouteComponent,
});

export function RouteComponent() {
  const [isLeaving, setIsLeaving] = useState(false);
  const { mutate: signIn, isPending } = useSignIn();

  const form = useForm<SignInSchema>({
    resolver: zodResolver(authSchemas.signIn),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
    },
    shouldUseNativeValidation: false,
  });

  const handleSubmit = (data: SignInSchema) => {
    signIn(data, {
      onSuccess: () => {
        setIsLeaving(true);
      },
    });
  };

  const signInForm = (
    <motion.form
      initial={{ opacity: 0, y: 40 }}
      animate={
        isLeaving ? { opacity: 0, y: -40, scale: 0.98 } : { opacity: 1, y: 0, scale: 1 }
      }
      transition={{ duration: 0.4 }}
      variants={animations.fadeUp}
      id="form"
      onSubmit={form.handleSubmit(handleSubmit)}
      className={cn(
        'bg-background/80 md:bg-background-60',
        'h-3/4 w-full max-w-lg px-4 py-6',
        'rounded-[20px]',
        'shadow-md shadow-black/50',
        'flex flex-col gap-6 justify-center',
      )}
    >
      <FormInput
        md={12}
        lg={12}
        name="email"
        control={form.control}
        label="E-mail"
        type="email"
        leftAddon={<Icon name="mail" />}
        placeholder="john.doe@example.com"
      />
      <FormInput
        name="password"
        control={form.control}
        label="Senha"
        type="password"
        leftAddon={<Icon name="lock" />}
        placeholder="********"
      />
      <FormSwitch
        name="rememberMe"
        control={form.control}
        label="Lembrar-me"
        horizontal
      />
      <Button
        type="submit"
        isLoading={isPending || isLeaving}
        disabled={isPending}
        form="form"
        className="flex items-center gap-2"
      >
        <Icon name="save" />
        Salvar
      </Button>
    </motion.form>
  );

  return (
    <div
      className={cn(
        'flex items-center h-screen px-4 md:px-0',
        'relative z-0',
        'bg-[url("../public/bg.jpg")] md:bg-cover md:bg-center',
      )}
    >
      <ThemeToggle className="fixed top-5 right-5 z-90" />
      <div className="absolute w-full h-screen top-0 left-0 z-10 bg-black/60 dark:bg-black/80" />
      <div
        className={
          'hidden md:flex flex-col gap-6 justify-center items-start px-24 w-1/2 h-screen relative z-20'
        }
      >
        <motion.img
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          src="/assets/logo.png"
          alt={CONFIG.APP_NAME}
          className="w-64"
        />
        <motion.h1
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl font-bold text-white"
        >
          Bem-vindo de volta!
        </motion.h1>
        <motion.h2
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="text-2xl max-w-lg italic text-gray-300"
        >
          Que bom ver você aqui! Gerencie todas as suas vendas aqui.
        </motion.h2>
      </div>
      <div
        className={
          'flex flex-col items-center justify-center w-full md:w-1/2 h-screen relative z-20'
        }
      >
        {signInForm}
      </div>
    </div>
  );
}
